<template>
    <v-container>
        <v-row>
            <v-col>
                <h1>Mobiz Demo - Employees</h1>
                <v-btn color="primary" @click="showAddEmployeeModal">Add Employee</v-btn>
                <v-data-table :headers="headers" :items="employees" class="elevation-1">
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-btn small @click="editEmployee(item)">Edit</v-btn>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>

        <!-- Modal Dialog -->
        <v-dialog v-model="showModal" max-width="500px">
            <v-card>
                <v-card-title>
                    <span class="headline">{{ selectedEmployee.id ? 'Edit' : 'Add' }} Employee</span>
                </v-card-title>
                <v-card-text>
                    <v-form ref="form">
                        <v-text-field v-model="selectedEmployee.firstName" label="First Name" required></v-text-field>
                        <v-text-field v-model="selectedEmployee.lastName" label="Last Name" required></v-text-field>
                        <v-text-field v-model="selectedEmployee.email" label="Email" required></v-text-field>
                        <v-select v-model="selectedEmployee.employmentType"
                                  :items="employmentTypes"
                                  label="Employment Type"
                                  required></v-select>
                        <v-text-field v-model="selectedEmployee.employmentDate"
                                      label="Employment Date"
                                      type="date"
                                      required></v-text-field>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="blue darken-1" text @click="closeModal">Cancel</v-btn>
                    <v-btn color="blue darken-1" text @click="saveEmployee">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script lang="ts">
    import { defineComponent, ref, onMounted } from 'vue';
    import { EmployeeService, Employee, EmploymentType } from '@/services/EmployeeService';

    export default defineComponent({
      setup() {
        const employees = ref<Employee[]>([]);
        const showModal = ref(false);
        const selectedEmployee = ref<Employee>({
          id: '',
          firstName: '',
          lastName: '',
          email: '',
          employmentType: EmploymentType.Permanent,
          employmentDate: '',
        });
        const headers = ref([
          { text: 'First Name', value: 'firstName' },
          { text: 'Last Name', value: 'lastName' },
          { text: 'Email', value: 'email' },
          { text: 'Employment Type', value: 'employmentType' },
          { text: 'Employment Date', value: 'employmentDate' },
          { text: 'Actions', value: 'actions', sortable: false },
        ]);

        const employmentTypes = ref([
          EmploymentType.Permanent,
          EmploymentType.Intern,
          EmploymentType.Freelance,
        ]);

        const fetchEmployees = async () => {
          employees.value = await EmployeeService.getEmployees();
        };

        const showAddEmployeeModal = () => {
          selectedEmployee.value = {
            id: '',
            firstName: '',
            lastName: '',
            email: '',
            employmentType: EmploymentType.Permanent,
            employmentDate: '',
          };
          showModal.value = true;
        };

        const editEmployee = (employee: Employee) => {
          selectedEmployee.value = { ...employee };
          showModal.value = true;
        };

        const saveEmployee = async () => {
          if (selectedEmployee.value.id) {
            await EmployeeService.updateEmployee(selectedEmployee.value);
          } else {
            await EmployeeService.addEmployee(selectedEmployee.value);
          }
          closeModal();
          fetchEmployees();
        };

        const closeModal = () => {
          showModal.value = false;
        };

        onMounted(fetchEmployees);

        return {
          employees,
          showModal,
          selectedEmployee,
          headers,
          employmentTypes,
          showAddEmployeeModal,
          editEmployee,
          saveEmployee,
          closeModal,
        };
      },
    });
</script>

<style scoped>
    button {
        margin-left: 10px;
    }

    table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 20px;
    }

    table, th, td {
        border: 1px solid black;
    }

    th, td {
        padding: 8px;
        text-align: left;
    }

    th {
        background-color: #f2f2f2;
    }
</style>
